import {
  isSoftwareJobComplete,
  isSoftwareViewJob,
  sortJobsBySoftwareDue,
} from '../../JobSchedulePage/utils/softwareViewUtils';

export const CONTROLS_GROUP_OPTIONS = [
  { value: 'assignedUser', label: 'Assigned User' },
  { value: 'status', label: 'Status' },
  { value: 'softwareType', label: 'Software Type' },
];

export const getControlsEngineerName = (job) => {
  const engineerName = typeof job?.ControlsEngineer === 'string'
    ? job.ControlsEngineer.trim()
    : '';

  return engineerName || 'Unassigned';
};

export const getControlsWorkspaceJobs = (jobs) => {
  if (!Array.isArray(jobs)) {
    return [];
  }

  return sortJobsBySoftwareDue(
    jobs.filter((job) => isSoftwareViewJob(job, { includeCompleted: true }))
  );
};

export const buildControlsEngineerOptions = (controlOptions = [], jobs = []) => {
  const engineerNames = new Set();

  controlOptions.forEach((option) => {
    if (option?.displayName) {
      engineerNames.add(option.displayName);
    }
  });

  jobs.forEach((job) => {
    engineerNames.add(getControlsEngineerName(job));
  });

  return ['all', ...Array.from(engineerNames).sort((left, right) => left.localeCompare(right))];
};

export const filterControlsJobs = (jobs, filters = {}) => {
  const { selectedEngineer = 'all', showCompleted = true } = filters;

  if (!Array.isArray(jobs)) {
    return [];
  }

  return jobs.filter((job) => {
    if (!showCompleted && isSoftwareJobComplete(job)) {
      return false;
    }

    if (selectedEngineer !== 'all' && getControlsEngineerName(job) !== selectedEngineer) {
      return false;
    }

    return true;
  });
};

const getGroupLabel = (job, groupBy = 'assignedUser') => {
  if (groupBy === 'status') {
    const statusValue = typeof job?.JobStatus === 'string' ? job.JobStatus.trim() : '';
    return statusValue || 'Unknown Status';
  }

  if (groupBy === 'softwareType') {
    const softwareType = typeof job?.SoftwareType === 'string' ? job.SoftwareType.trim() : '';
    return softwareType || 'Unknown Software Type';
  }

  return getControlsEngineerName(job);
};

export const groupControlsJobs = (jobs, groupBy = 'assignedUser') => {
  if (!Array.isArray(jobs)) {
    return [];
  }

  const activeGroups = new Map();
  const completedJobs = [];

  jobs.forEach((job) => {
    if (isSoftwareJobComplete(job)) {
      completedJobs.push(job);
      return;
    }

    const groupLabel = getGroupLabel(job, groupBy);

    if (!activeGroups.has(groupLabel)) {
      activeGroups.set(groupLabel, []);
    }

    activeGroups.get(groupLabel).push(job);
  });

  const groupedItems = Array.from(activeGroups.entries())
    .sort(([leftLabel], [rightLabel]) => leftLabel.localeCompare(rightLabel))
    .map(([label, groupJobs]) => ({
      key: label,
      label,
      jobs: sortJobsBySoftwareDue(groupJobs),
      isCompletedGroup: false,
    }));

  if (completedJobs.length > 0) {
    groupedItems.push({
      key: 'completed-group',
      label: 'Completed',
      jobs: sortJobsBySoftwareDue(completedJobs),
      isCompletedGroup: true,
    });
  }

  return groupedItems;
};

export const getControlsSummary = (jobs) => {
  if (!Array.isArray(jobs)) {
    return {
      total: 0,
      completed: 0,
      active: 0,
      unscheduled: 0,
    };
  }

  return jobs.reduce(
    (summary, job) => {
      summary.total += 1;

      if (isSoftwareJobComplete(job)) {
        summary.completed += 1;
      } else {
        summary.active += 1;
      }

      if (!job?.SoftwareDue) {
        summary.unscheduled += 1;
      }

      return summary;
    },
    {
      total: 0,
      completed: 0,
      active: 0,
      unscheduled: 0,
    }
  );
};

export const getControlsStatusVariant = (job) => {
  return isSoftwareJobComplete(job) ? 'success' : 'warning';
};

export const getControlsStatusLabel = (job) => {
  return isSoftwareJobComplete(job) ? 'Complete' : 'Active';
};

export const getCalendarRangeSummary = (jobs, rangeDates = []) => {
  const rangeDateSet = new Set(rangeDates);

  return getControlsSummary(
    jobs.filter((job) => job?.SoftwareDue && rangeDateSet.has(new Date(job.SoftwareDue).toISOString().slice(0, 10)))
  );
};